import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import SelfServeAnalyticsImage from './SelfServeAnalyticsImage'

const copy = {
  title: `Self-serve analytics`,
  description: `Get instant customer insights that are accessible organization-wide.`,
  elements: [
    {
      title: `No more manual data processing`,
      description: `Pulling data manually can bottleneck your business, delaying critical decisions. Segment helps you collect and clean data from any source and create unified customer profiles that are accessible to every team in your organization.`,
    },
    {
      title: `Comprehensive insights without the wait`,
      description: `Empower your teams with immediate access to clean, consented customer data and seamless integration with your existing analytics platforms accelerating insights and enhancing decision-making.`,
    },
    {
      title: `Transform your campaigns with robust data`,
      description: `Segment provides more than just data access; it equips each byte with the potential to unlock insights, guiding more informed decisions across your organization.`,
    },
  ],
  buttonText: `Explore Unify`,
  buttonUrl: `/product/unify/`,
}

const SelfServeAnalytics = () => {
  const theme = useTheme()
  const pageConfig = usePageConfig()
  return (
    <Wrapper id="self-serve-analytics">
      <StyledSectionBaseTitle variant="noCopy">
        <Section.Contents>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.title}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
          </CopyTextBase>
        </Section.Contents>
      </StyledSectionBaseTitle>
      <StyledSectionBaseContent reverse={true} variant="2Col">
        <Section.Contents>
          <SelfServeAnalyticsImage />
        </Section.Contents>
        <Section.Copy alignSelf="center">
          <ColumnTrack>
            {copy.elements.map(({ title, description }, key) => (
              <StyledFeatureBoxBase key={key} alignment="left" appearance="twilioLight" headerPosition="left">
                <FeatureBox.Body>
                  <FeatureBox.Title>{title}</FeatureBox.Title>
                  <FeatureBox.Text
                    tokens={{
                      color: theme.palette.twilio.gray[80],
                    }}
                    variant="pMedium"
                  >
                    {description}
                  </FeatureBox.Text>
                  {key === copy.elements.length - 1 && (
                    <CopyText.Link href={getAbsoluteUrl(copy.buttonUrl, pageConfig)}>{copy.buttonText}</CopyText.Link>
                  )}
                </FeatureBox.Body>
              </StyledFeatureBoxBase>
            ))}
          </ColumnTrack>
        </Section.Copy>
      </StyledSectionBaseContent>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  gap: 0;
  grid-template-columns: 22px auto;
`

const StyledSectionBaseTitle = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 0;
  }
`

const StyledSectionBaseContent = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 80px;
  }
`

export default SelfServeAnalytics
